import LandingPage from '@/pages/LandingPage';
import { CallbackPage } from '@/pages/CallbackPage';
import { AppHomePage, OnboardingPage } from '@/pages/AppPages';
import { TicketsPage } from '@/pages/TicketsPage';
import { CreateTicketPage } from '@/pages/CreateTicketPage';
import { TicketDetailPage } from '@/pages/TicketDetailPage';
import { KbArticlePage } from '@/pages/KbArticlePage';
import {
  DashboardPage,
  QueuesPage,
  SettingsPage,
  NotificationsPage,
  KbPage,
} from '@/pages/OtherPages';
import { AuthGuard } from '@/components/AuthGuard';
import { ProductTourProvider } from '@/components/organisms/ProductTour/ProductTour';
import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router';

function RootLayout() {
  return <Outlet />;
}

function AuthenticatedLayout() {
  return (
    <AuthGuard>
      <ProductTourProvider>
        <Outlet />
      </ProductTourProvider>
    </AuthGuard>
  );
}

const rootRoute = createRootRoute({
  component: RootLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: LandingPage,
});

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/callback',
  component: CallbackPage,
});

const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  component: AuthenticatedLayout,
});

const appRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/app',
  component: AppHomePage,
});

const onboardingRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/onboarding',
  component: OnboardingPage,
});

const ticketsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/tickets',
  component: TicketsPage,
});

const createTicketRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/tickets/new',
  component: CreateTicketPage,
});

const ticketDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/tickets/$ticketId',
  component: TicketDetailPage,
});

const dashboardRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/dashboard',
  component: DashboardPage,
});

const queuesRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/queues',
  component: QueuesPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings',
  component: SettingsPage,
});

const notificationsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/notifications',
  component: NotificationsPage,
});

const kbRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/kb',
  component: KbPage,
});

const kbArticleRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/kb/$articleId',
  component: KbArticlePage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  callbackRoute,
  authenticatedRoute.addChildren([
    appRoute,
    onboardingRoute,
    ticketsRoute,
    createTicketRoute,
    ticketDetailRoute,
    dashboardRoute,
    queuesRoute,
    settingsRoute,
    notificationsRoute,
    kbRoute,
    kbArticleRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
